'use client';

import { cn } from '@/lib/utils';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';

import checkbox from '@/public/challenge/checkbox.svg';
import { motion } from 'framer-motion';
import FAQList from '@/components/global/ui/FAQList';
import { PortableTextBlock } from 'sanity';
import { PageAffiliateSchema } from '@/sanity/pages/PageAffiliateTemplate';
import iqicon from '@/public/affiliate/iqIcon.svg';
import cardDots from '@/public/affiliate-template/card-dot.png';
import { CTAButton } from '@/shadcn/ui/button';
import { Fragment } from 'react';
import ScrollingBanner from '../global/ScrollingBanner';

function Quote() {
  return (
    <svg
      width="32"
      height="24"
      viewBox="0 0 32 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.6893 24L18.6893 14.1453C18.6893 6.54 23.664 1.38533 30.6667 -5.54841e-06L31.9933 2.86799C28.7507 4.09066 26.6667 7.71866 26.6667 10.6667L32 10.6667L32 24L18.6893 24ZM1.90735e-06 24L1.32777e-06 14.1453C8.80487e-07 6.54 4.99733 1.384 12 -3.11757e-06L13.328 2.868C10.084 4.09066 8 7.71867 8 10.6667L13.3107 10.6667L13.3107 24L1.90735e-06 24Z"
        fill="#12F0B4"
      />
    </svg>
  );
}

export default function ExclusiveSection({
  data,
  className,
}: {
  data: PageAffiliateSchema;
  className?: string;
}) {
  return (
    <section
      className={cn('relative overflow-clip px-[1rem] lg:px-[5rem]', className)}
    >
      <div className="relative bg-[#001B14] pb-[20rem] px-[3rem] pt-[7rem] lg:pt-[17.5rem] lg:pb-[24rem]">
        <div className="absolute inset-x-0 bottom-0 z-50 border-t overflow-clip h-[10rem] border-t-white/20">
          <ScrollingBanner
            theme="dark"
            className="bg-transparent border-y-0"
            words={data.scrollingWords?.words}
            wordsI18n={data.scrollingWords?.wordsI18n}
          />
        </div>

        <div className="w-[min(86.125rem,100%)] mx-auto flex flex-col relative  ">
          {data.exclusiveTitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext mb-[4.375rem] richtext-start lg:richtext-center prose-h2:tracking-[-0.225rem] lg:prose-h2:tracking-[-0.35rem] prose-h6:text-white/60 prose-h6:text-[2rem] prose-h6:font-blacker prose-h6:italic prose-h6:mb-[2rem] prose-headings:text-white"
            >
              <PortableText
                value={data.exclusiveTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
          {data.exclusiveTestimonials && (
            <div className="flex flex-col gap-[2rem] mb-[2.25rem]">
              {data.exclusiveTestimonials.map((item, index) => (
                <motion.div
                  key={'exclusiveTest' + index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="flex bg-white/[8%]  border-black/10 border rounded-[0.25rem] py-[5rem] lg:px-[6rem] px-[5rem]  gap-[4rem] relative overflow-clip flex-col"
                >
                  <div className="absolute inset-0 left-0 h-full w-[0.375rem] bg-[#12F0B4]" />
                  <Image
                    src={cardDots}
                    alt=""
                    className="object-cover absolute inset-0 w-full h-full z-[1]"
                  />
                  <div className="relative w-[4rem] shrink-0 z-[5]">
                    <Quote />
                  </div>
                  <div className="flex relative flex-col gap-[3rem] z-[5]">
                    <div className="richtext richtext-start prose-p:text-white prose-p:text-[2rem] prose-p:not-italic lg:prose-p:text-[2.25rem]">
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    </div>
                    <span className="uppercase border-l border-l-white/40 pl-[1.5rem] text-white/40 font-geist-mono font-[600] prose-p:text-[1.75rem]">
                      {item.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="flex flex-col w-full lg:grid lg:grid-cols-2 gap-[2rem]">
            {data?.exclusiveItems &&
              data?.exclusiveItems.map((item, index) => (
                <Fragment key={'f' + index}>
                  <motion.div
                    key={index}
                    className="flex items-center w-full lg:flex-col lg:justify-between lg:items-start gap-[2.25rem] lg:p-[3rem] lg:bg-white/5 lg:h-[21.875rem] lg:gap-[3.25rem]"
                    initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
                    whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.1,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                  >
                    {item?.icon && (
                      <Image
                        src={item.icon}
                        alt=""
                        className="!w-[6rem] shrink-0"
                      />
                    )}

                    <p className="m-0 text-white font-geist text-[2.25rem] leading-[135%]">
                      {item.text}
                    </p>
                  </motion.div>

                  {index !== data.exclusiveItems.length - 1 && (
                    <hr className="w-full border-t lg:hidden border-white/10" />
                  )}
                </Fragment>
              ))}
          </div>
          <div className="w-full lg:mt-0 mt-[2rem]">
            <CTAButton
              data={data.exclusiveCta}
              className="mx-auto w-full mt-[3rem] lg:w-fit lg:min-w-[40rem]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
